import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import Logo from "../components/Logo";
import { homeFor } from "../lib/home";

const MODES = [
  { id: "member", label: "I'm booking classes" },
  { id: "client", label: "I have a coach code" },
  { id: "coach", label: "I run a business" },
];

const COPY = {
  member: { kicker: "[ Book classes anywhere ]", title: "Create your free account.", sub: "One account for every studio and coach on Traina. Book classes, keep your credits, manage everything in one place." },
  client: { kicker: "[ Join your coach ]", title: "Create account.", sub: "Got a 1:1 access code from your coach? Drop it below and you're in." },
  coach: { kicker: "[ Start your business ]", title: "Create business account.", sub: "Your own portal — clients, scheduling, billing, branding. Free to set up." },
};

export default function Register() {
  const { register, registerMember, registerCoach } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefill = (params.get("code") || "").toUpperCase();
  const next = params.get("next") || "";
  const as = params.get("as");
  const [mode, setMode] = useState(prefill ? "client" : as === "coach" ? "coach" : as === "client" ? "client" : "member");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", access_code: prefill, business_name: "" });
  const [loading, setLoading] = useState(false);
  const [codeState, setCodeState] = useState({ status: "idle", trainer: "" }); // idle | checking | valid | invalid

  const set = (k) => (e) => {
    const v = k === "access_code" ? e.target.value.toUpperCase().replace(/\s/g, "") : k === "email" ? e.target.value.trim() : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  // Debounced live check of the access code (client mode only)
  useEffect(() => {
    if (mode !== "client") return;
    const code = form.access_code.trim();
    if (code.length < 4) {
      setCodeState({ status: "idle", trainer: "" });
      return;
    }
    setCodeState({ status: "checking", trainer: "" });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/access-code/${encodeURIComponent(code)}`);
        setCodeState({ status: "valid", trainer: data.trainer_name });
      } catch (_e) {
        setCodeState({ status: "invalid", trainer: "" });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [form.access_code, mode]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "client" && codeState.status !== "valid") {
      toast.error("Enter a valid coach access code to continue");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form, email: form.email.trim() };
      if (mode === "coach") {
        const u = await registerCoach(payload);
        toast.success(`Welcome, ${u.name} — your coaching portal is ready`);
        navigate("/trainer", { replace: true });
      } else if (mode === "member") {
        const u = await registerMember(payload);
        toast.success(`Welcome, ${u.name} — you're all set to book`);
        navigate(next.startsWith("/") ? next : homeFor(u), { replace: true });
      } else {
        const u = await register(payload);
        toast.success(`Welcome, ${u.name} — you're now training with ${codeState.trainer}`);
        navigate("/client", { replace: true });
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const copy = COPY[mode];

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white p-4 sm:p-6" data-testid="register-page">
      <div className="w-full max-w-md card-flat p-6 sm:p-10">
        <Link to="/" className="flex items-center gap-3 mb-8">
          <Logo size={28} />
          <div>
            <div className="font-display text-xl leading-none">TRAINA</div>
            <div className="tracker text-zinc-500 mt-1">Run your training business with confidence</div>
          </div>
        </Link>

        <div className="grid grid-cols-3 gap-1 mb-8 border border-white/10 p-1" data-testid="register-mode-toggle">
          {MODES.map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setMode(m.id)}
              className={`tracker text-[9px] sm:text-[10px] py-2.5 transition-colors ${mode === m.id ? "bg-brand text-white" : "text-zinc-500 hover:text-zinc-300"}`}
              data-testid={`register-mode-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="tracker mb-3 text-brand">{copy.kicker}</div>
        <h1 className="font-display text-4xl mb-2">{copy.title}</h1>
        <p className="text-zinc-400 mb-8 text-sm">{copy.sub}</p>

        <form onSubmit={submit} className="space-y-4" data-testid="register-form">
          {mode === "client" && (
            <div>
              <label className="tracker block mb-2">Coach access code</label>
              <input
                required
                value={form.access_code}
                onChange={set("access_code")}
                placeholder="e.g. A4F9C2K7"
                maxLength={12}
                className={`w-full px-4 py-3 border font-mono uppercase tracking-widest ${codeState.status === "valid" ? "border-emerald-500/60" : codeState.status === "invalid" ? "border-red-500/60" : ""}`}
                data-testid="register-code-input"
              />
              <div className="mt-2 min-h-[20px] text-xs flex items-center gap-1" data-testid="code-status">
                {codeState.status === "checking" && <span className="text-zinc-500">Checking…</span>}
                {codeState.status === "valid" && (
                  <span className="text-emerald-400 flex items-center gap-1">
                    <CheckCircle size={12} weight="fill" /> Coach: <span className="font-semibold">{codeState.trainer}</span>
                  </span>
                )}
                {codeState.status === "invalid" && (
                  <span className="text-red-400 flex items-center gap-1">
                    <Warning size={12} weight="fill" /> No coach found with that code
                  </span>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="tracker block mb-2">Full name</label>
            <input required value={form.name} onChange={set("name")} className="w-full px-4 py-3 border" data-testid="register-name-input" />
          </div>
          {mode === "coach" && (
            <div>
              <label className="tracker block mb-2">Business name (optional)</label>
              <input value={form.business_name} onChange={set("business_name")} placeholder="e.g. Apex Coaching" className="w-full px-4 py-3 border" data-testid="register-business-input" />
            </div>
          )}
          <div>
            <label className="tracker block mb-2">Email</label>
            <input required type="email" value={form.email} onChange={set("email")} className="w-full px-4 py-3 border" data-testid="register-email-input" />
            {mode === "member" && <p className="text-[11px] text-zinc-500 mt-1.5">Moving from another booking app? Use the same email so your history and class credits follow you.</p>}
          </div>
          <div>
            <label className="tracker block mb-2">Phone (optional)</label>
            <input value={form.phone} onChange={set("phone")} placeholder="+1 555 555 0123" className="w-full px-4 py-3 border" data-testid="register-phone-input" />
          </div>
          <div>
            <label className="tracker block mb-2">Password</label>
            <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="w-full px-4 py-3 border" data-testid="register-password-input" />
          </div>
          <button
            type="submit"
            disabled={loading || (mode === "client" && codeState.status !== "valid")}
            className="btn-primary w-full justify-center"
            data-testid="register-submit-button"
          >
            {loading ? "Creating…" : mode === "coach" ? "Create business account" : "Create account"} <ArrowRight size={16} weight="bold" />
          </button>
        </form>

        <p className="mt-6 text-sm text-zinc-500">
          Already have one? <Link to={next ? `/login?next=${encodeURIComponent(next)}` : "/login"} className="text-brand hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
